/**
 * Day grouping for the activity feed, on a FIXED Philippine boundary (E9 ruling G7 / F14).
 *
 * ## Why not the browser's timezone
 *
 * The feed's day headers say `Today` and `Yesterday`. If those were computed in the viewer's local zone, the
 * same event would sit under different headers for two people looking at the same record — and a warehouse
 * supervisor reviewing a night shift from a laptop still set to another zone would see picks split across two
 * "days" that never existed on the floor. The business day here is a Philippine day; it does not change
 * because someone travelled.
 *
 * Every operational timestamp in this system is already read in PHT (`ToPhilippineTime` on the server stamps
 * document dates the same way), so this is the existing rule applied to the feed rather than a new one.
 *
 * ## Why a fixed +08:00 rather than `Intl` with a zone
 *
 * The Philippines has observed **no DST since 1978** and has a single zone. A fixed offset is therefore exact,
 * and it is also *deterministic under test* — `Intl.DateTimeFormat` with a timeZone depends on the host's
 * ICU data, which differs between a CI container and a developer's machine. If the country ever adopts DST
 * this becomes wrong in one place, with one test, rather than everywhere.
 */

/** UTC+08:00, in milliseconds. */
const PHT_OFFSET_MS = 8 * 60 * 60 * 1000;

/** `YYYY-MM-DD` of the PHT calendar day an instant falls on. Sorts lexicographically. */
export function phtDayKey(iso: string): string {
  const shifted = new Date(new Date(iso).getTime() + PHT_OFFSET_MS);
  // getUTC* AFTER shifting: the shift moved the instant so that UTC fields now read PHT wall-clock.
  // Using local getters here would apply the host's offset a second time.
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The sticky header's text for a day key.
 *
 * `Today` / `Yesterday` carry the date after a `·` (design §1.4) — the relative word alone makes a feed
 * ambiguous the moment someone leaves a tab open across midnight.
 *
 * @param now Injected so the label is testable without faking the clock, and so a long-lived tab can be
 *            re-rendered against a fresh `now` rather than a value captured at mount.
 */
export function phtDayLabel(dayKey: string, now: Date = new Date()): string {
  const todayKey = phtDayKey(now.toISOString());
  const yesterdayKey = phtDayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString());

  const pretty = formatDayKey(dayKey, phtDayKey(now.toISOString()).slice(0, 4));
  if (dayKey === todayKey) return `Today · ${pretty}`;
  if (dayKey === yesterdayKey) return `Yesterday · ${pretty}`;
  return pretty;
}

/**
 * `2026-08-18` → `Aug 18`, or `Aug 18, 2025` once the year stops being the current one.
 *
 * The screenshots settle the format: `JUL 22` and `TODAY · AUG 13` carry no year. Repeating the current year
 * on every header is noise in a feed that is almost always looking at the last few days — but a feed paged
 * back far enough that the year HAS changed must not be ambiguous, so the year returns exactly when it starts
 * carrying information.
 *
 * Built from the key's own digits rather than by re-parsing it into a `Date`: `new Date('2026-08-18')` is
 * midnight UTC, which is the previous day in half the world's local zones — the precise off-by-one this
 * whole module exists to avoid.
 */
function formatDayKey(dayKey: string, currentYear: string): string {
  const [y, m, d] = dayKey.split('-');
  const base = `${MONTHS[Number(m) - 1]} ${Number(d)}`;
  return y === currentYear ? base : `${base}, ${y}`;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * `Sep 29, 2026` — the PHT calendar date of an instant, ALWAYS with its year.
 *
 * The day header drops the current year because it sits above a whole run of rows. This is for a single row's
 * hover, which is read on its own — copied into a message, or looked at once the header has scrolled away — so
 * it carries the complete date (owner, 2026-10-05).
 */
export function phtFullDate(iso: string): string {
  const [y, m, d] = phtDayKey(iso).split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
}

/** `4:58 PM`, in Philippine time regardless of where the viewer is. */
export function phtTimeOfDay(iso: string): string {
  const d = new Date(new Date(iso).getTime() + PHT_OFFSET_MS);
  const hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${minutes} ${suffix}`;
}

/** `4:58:07 PM`, in Philippine time — for a list whose entries can be seconds apart (one Submit's picks). */
export function phtTimeWithSeconds(iso: string): string {
  const d = new Date(new Date(iso).getTime() + PHT_OFFSET_MS);
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  return phtTimeOfDay(iso).replace(/(\d+:\d+)/, `$1:${seconds}`);
}

/** `Sep 29` — the PHT month and day, for a one-line summary that already sits beside its year. */
export function phtMonthDay(iso: string): string {
  const [, m, d] = phtDayKey(iso).split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)}`;
}

/** `Sep 29, 2026, 4:16 PM PHT` — a complete, zone-named moment for a hover that is read on its own. */
export function phtDateTime(iso: string): string {
  return `${phtFullDate(iso)}, ${phtTimeOfDay(iso)} PHT`;
}

/**
 * Split rows into consecutive day runs, preserving the server's newest-first order.
 *
 * Runs, not a `groupBy`: the list is already sorted, so grouping by key would be a no-op that quietly
 * REORDERS if it ever were not — and under async arrival a late row landing out of order should show up as a
 * visible oddity, not be silently tidied into a group it did not arrive in.
 */
export function groupByPhtDay<T extends { occurredAtUtc: string }>(
  rows: readonly T[],
): { dayKey: string; rows: T[] }[] {
  const groups: { dayKey: string; rows: T[] }[] = [];

  for (const row of rows) {
    const dayKey = phtDayKey(row.occurredAtUtc);
    const last = groups[groups.length - 1];
    if (last && last.dayKey === dayKey) last.rows.push(row);
    else groups.push({ dayKey, rows: [row] });
  }

  return groups;
}
